/* Theme custom file */

/** Gap between the ⓘ and the nearest edge of the panel, caret included. */
const TRIGGER_GAP = 8;

/** Smallest gap kept between the panel and the edge of the viewport. */
const VIEWPORT_MARGIN = 16;

/** Floor for the readable area, below which scrolling beats shrinking further. */
const MIN_BODY_HEIGHT = 96;

/**
 * Places the reference price panel against its trigger.
 *
 * The panel is a popover, so the browser paints it in the top layer and owns
 * opening, dismissal and focus. That is the whole point: the quick-add modal
 * clips anything positioned inside it, and a panel in its flow pushes the sizes
 * and the add-to-cart button down the page. Neither reaches the top layer.
 *
 * The top layer positions against the viewport rather than the trigger, so the
 * placement is this element's job: centre the panel on the ⓘ, put it on the
 * side that holds it, and keep the whole panel on screen.
 */
class PriceReferenceTooltip extends HTMLElement {
  /** @type {AbortController | null} Live only while the element is connected. */
  #controller = null;

  /** @type {AbortController | null} Live only while the panel is open. */
  #openController = null;

  connectedCallback() {
    this.trigger = this.querySelector('.price-tooltip__trigger');
    this.panel = this.querySelector('.price-tooltip__panel');
    this.body = this.querySelector('.price-tooltip__body');
    this.caret = this.querySelector('.price-tooltip__caret');

    if (!this.trigger || !this.panel) return;

    // Built here rather than as a field, which would run once per element and
    // leave a reattached element listening on an already aborted signal.
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    // beforetoggle is synchronous, so the panel is placed before the browser
    // paints it. toggle runs again once it is laid out, when its height is
    // finally measurable and the above/below choice can be confirmed.
    this.panel.addEventListener('beforetoggle', this.#handleBeforeToggle, { signal });
    this.panel.addEventListener('toggle', this.#handleToggle, { signal });

    // Opt-in hover for surfaces that want it. Mouse only: a tap fires pointerenter
    // then click, and the click would toggle the panel straight back shut.
    if (this.hasAttribute('data-hover')) {
      this.trigger.addEventListener('pointerenter', this.#handlePointerEnter, { signal });
      this.trigger.addEventListener('pointerleave', this.#handlePointerLeave, { signal });
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#stopTracking();
  }

  /** @param {Event} event */
  #handleBeforeToggle = (event) => {
    if (/** @type {ToggleEvent} */ (event).newState === 'open') this.#place();
  };

  /** @param {Event} event */
  #handleToggle = (event) => {
    if (/** @type {ToggleEvent} */ (event).newState === 'open') {
      this.#place();
      this.#startTracking();
    } else {
      this.#stopTracking();
    }
  };

  /** @param {PointerEvent} event */
  #handlePointerEnter = (event) => {
    if (event.pointerType === 'mouse' && !this.panel.matches(':popover-open')) this.panel.showPopover();
  };

  /** @param {PointerEvent} event */
  #handlePointerLeave = (event) => {
    if (event.pointerType === 'mouse' && this.panel.matches(':popover-open')) this.panel.hidePopover();
  };

  /**
   * While the panel is open it is pinned to the viewport but anchored to an
   * element that moves with the page, so it has to follow any scroll or resize.
   * Capture catches scrolling inside the modal as well as the window.
   */
  #startTracking() {
    this.#stopTracking();
    this.#openController = new AbortController();
    const { signal } = this.#openController;

    document.addEventListener('scroll', this.#place, { capture: true, passive: true, signal });
    window.addEventListener('resize', this.#place, { passive: true, signal });
  }

  #stopTracking() {
    this.#openController?.abort();
    this.#openController = null;
  }

  #place = () => {
    const { trigger, panel, body, caret } = this;
    if (!trigger || !panel) return;

    // Measure unconstrained. A cap left over from the last placement would make
    // the disclaimer look like it fits wherever it was put then.
    if (body) body.style.maxHeight = '';

    const anchor = trigger.getBoundingClientRect();
    // Zero on the first beforetoggle, when the panel is still display: none.
    // The toggle pass runs again once it is laid out, and both land before the
    // browser paints the open state.
    const { width, height } = panel.getBoundingClientRect();

    const center = anchor.left + anchor.width / 2;
    const rightmost = Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN);
    const left = Math.min(Math.max(center - width / 2, VIEWPORT_MARGIN), rightmost);

    // Above by preference, which is where the product page puts it. The
    // disclaimer runs to several paragraphs though, so when it does not fit
    // there it goes to whichever side holds more of it and scrolls the rest.
    const roomAbove = anchor.top - TRIGGER_GAP - VIEWPORT_MARGIN;
    const roomBelow = window.innerHeight - anchor.bottom - TRIGGER_GAP - VIEWPORT_MARGIN;
    const above = height <= roomAbove || roomAbove > roomBelow;
    const room = Math.max(above ? roomAbove : roomBelow, MIN_BODY_HEIGHT);

    if (body) {
      // The caret shares the column, so it keeps its own height out of the cap.
      body.style.maxHeight = `${Math.max(room - (caret?.offsetHeight ?? 0), MIN_BODY_HEIGHT)}px`;
      // A region the pointer can scroll has to be reachable by keyboard too. A
      // panel that fits adds no tab stop.
      if (body.scrollHeight > body.clientHeight) body.tabIndex = 0;
      else body.removeAttribute('tabindex');
    }

    // Re-measure: capping the body may have shortened the panel, and placing it
    // above works back from its bottom edge.
    const placedHeight = panel.getBoundingClientRect().height;
    const top = above ? anchor.top - TRIGGER_GAP - placedHeight : anchor.bottom + TRIGGER_GAP;

    panel.dataset.placement = above ? 'above' : 'below';
    panel.style.left = `${left}px`;
    panel.style.top = `${top}px`;
    // Whatever the viewport clamp took off the centring, the caret gives back,
    // so it keeps pointing at the ⓘ.
    panel.style.setProperty('--price-tooltip-caret-shift', `${center - (left + width / 2)}px`);
  };
}

if (!customElements.get('price-reference-tooltip')) {
  customElements.define('price-reference-tooltip', PriceReferenceTooltip);
}
